import type { FormEvent } from 'react';

import type { ApplicationInvoice, BillingData, Tenant } from '../domain-types';

export function BillingPanel({
  visible,
  tenant,
  billingData,
  billingContactName,
  billingEmail,
  billingAddress,
  billingBusy,
  onBillingContactNameChange,
  onBillingEmailChange,
  onBillingAddressChange,
  onSaveProfile,
  onStartCheckout,
  onDownloadInvoice,
}: {
  visible: boolean;
  tenant: Tenant | null;
  billingData: BillingData | null;
  billingContactName: string;
  billingEmail: string;
  billingAddress: string;
  billingBusy: boolean;
  onBillingContactNameChange: (value: string) => void;
  onBillingEmailChange: (value: string) => void;
  onBillingAddressChange: (value: string) => void;
  onSaveProfile: (event: FormEvent<HTMLFormElement>) => Promise<void>;
  onStartCheckout: () => Promise<void>;
  onDownloadInvoice: (invoice: ApplicationInvoice) => Promise<void>;
}) {
  if (!visible || !tenant) return null;
  return (
    <section className="billing-panel" aria-labelledby="billing-title">
      <div className="workspace-heading">
        <div>
          <p className="eyebrow">Organisation subscription</p>
          <h2 id="billing-title">Billing and invoices</h2>
        </div>
      </div>
      <div className="billing-grid">
        <form onSubmit={(event) => void onSaveProfile(event)}>
          <label>
            Billing contact
            <input
              required
              maxLength={160}
              value={billingContactName}
              onChange={(event) => onBillingContactNameChange(event.target.value)}
            />
          </label>
          <label>
            Billing email
            <input
              required
              type="email"
              value={billingEmail}
              onChange={(event) => onBillingEmailChange(event.target.value)}
            />
          </label>
          <label>
            Billing address — one line per row
            <textarea
              required
              rows={4}
              value={billingAddress}
              onChange={(event) => onBillingAddressChange(event.target.value)}
            />
          </label>
          <button disabled={billingBusy} type="submit">
            Save billing profile
          </button>
        </form>
        <div className="subscription-summary">
          <h3>Organisation plan</h3>
          {billingData?.plan ? (
            <p>
              {billingData.plan.displayName} ·{' '}
              {(billingData.plan.totalCents / 100).toLocaleString('en-AU', {
                style: 'currency',
                currency: 'AUD',
              })}{' '}
              per {billingData.plan.interval}
            </p>
          ) : (
            <p>Commercial pricing has not yet been activated.</p>
          )}
          {billingData?.stripeEnabled ? (
            <button disabled={billingBusy} type="button" onClick={() => void onStartCheckout()}>
              Subscribe with Stripe
            </button>
          ) : (
            <p className="empty-state">
              Stripe payments are safely disabled until pricing and business invoice details are
              approved.
            </p>
          )}
          <h3>quality.vet invoices</h3>
          {(billingData?.invoices ?? []).length === 0 ? (
            <p className="empty-state">No application invoices have been issued.</p>
          ) : (
            billingData?.invoices.map((invoice) => (
              <div className="billing-row" key={invoice.id}>
                <div>
                  <strong>{invoice.invoiceNumber}</strong>
                  <small>
                    {new Date(invoice.issuedAt).toLocaleDateString()} ·{' '}
                    {(invoice.totalCents / 100).toLocaleString('en-AU', {
                      style: 'currency',
                      currency: invoice.currency,
                    })}
                  </small>
                </div>
                <button type="button" onClick={() => void onDownloadInvoice(invoice)}>
                  Download
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
}
